/**
 * Shared WhatsApp bits — the contact-picker pill styling and the branded QR
 * card — used by both the officer schedule's "Send to WhatsApp" flow
 * (OfficerEditPopup) and the standalone Contacts panel, so the two look like
 * one feature rather than two different UIs that happen to both open WhatsApp.
 */
import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { chatLinkForClick, waMeLink } from '@/utils/whatsappLinks';

const PILL_COLORS = [
  { bg: '#ede9fe', text: '#5b21b6' }, // purple
  { bg: '#dbeafe', text: '#1e40af' }, // blue
  { bg: '#dcfce7', text: '#166534' }, // green
  { bg: '#ffedd5', text: '#9a3412' }, // orange
  { bg: '#fce7f3', text: '#9d174d' }, // pink
  { bg: '#ccfbf1', text: '#134e4a' }, // teal
  { bg: '#fef9c3', text: '#713f12' }, // yellow
  { bg: '#fee2e2', text: '#991b1b' }, // red
  { bg: '#e0e7ff', text: '#3730a3' }, // indigo
  { bg: '#cffafe', text: '#164e63' }, // cyan
];

// eslint-disable-next-line react-refresh/only-export-components
export function subteamColor(subteam: string) {
  let hash = 0;
  for (let i = 0; i < subteam.length; i++) hash = (hash * 31 + subteam.charCodeAt(i)) >>> 0;
  return PILL_COLORS[hash % PILL_COLORS.length];
}

// Font Awesome brands WhatsApp path — single compound path for the complete logo.
// `color` defaults to the official brand green; pass "white"/"currentColor" to
// drop it onto a colored badge instead.
export function WaIcon({ size, color = '#25D366' }: { size: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 448 512" xmlns="http://www.w3.org/2000/svg">
      <path fill={color} d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"/>
    </svg>
  );
}

/** A viewfinder-corners glyph — reads as "scan this" at a glance, unlike a
 * plain camera icon (which just says "photo"). */
function ScanIcon({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7V5a2 2 0 0 1 2-2h2" />
      <path d="M17 3h2a2 2 0 0 1 2 2v2" />
      <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
      <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
      <line x1="7" y1="12" x2="17" y2="12" />
    </svg>
  );
}

function CopyLabelIcon({ copied, size = 14 }: { copied: boolean; size?: number }) {
  if (copied) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

/**
 * A wa.me link rendered as a scannable QR, branded like an official
 * verification QR (Singpass, PayNow, …): a colored rounded-square frame, the
 * app's own logo badge sitting on top of the code's center, and the brand
 * wordmark underneath — so it reads as "this is a WhatsApp QR" at a glance
 * rather than a generic black-and-white square.
 */
function WhatsAppQrCard({ link, phone, size = 220 }: { link: string; phone?: string; size?: number }) {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!link) { setQrDataUrl(null); return; }
    let cancelled = false;
    // errorCorrectionLevel 'H' tolerates the center logo badge covering part
    // of the code without breaking scannability.
    QRCode.toDataURL(link, { width: size, margin: 1, errorCorrectionLevel: 'H', color: { dark: '#111b21', light: '#ffffff' } })
      .then((dataUrl) => { if (!cancelled) setQrDataUrl(dataUrl); })
      .catch(() => { if (!cancelled) setQrDataUrl(null); });
    return () => { cancelled = true; };
  }, [link, size]);

  const badgeSize = Math.round(size * 0.24);

  return (
    <div style={{
      display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 10,
      padding: '16px 16px 12px', background: 'white', borderRadius: 18,
      border: '3px solid #25D366', boxShadow: '0 4px 18px rgba(37,211,102,0.28)',
    }}>
      {/* The number itself, right on the card — the header above can show a
          contact's name instead, and once this is shared or screenshotted on
          its own there'd be nothing else on it saying which number it dials. */}
      {phone && (
        <span style={{ fontWeight: 700, fontSize: 15, color: '#111b21', letterSpacing: '0.02em' }}>
          {phone.startsWith('+') ? phone : `+${phone}`}
        </span>
      )}
      <div style={{ position: 'relative', width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {qrDataUrl ? (
          <img src={qrDataUrl} width={size} height={size} alt="WhatsApp QR code" style={{ display: 'block' }} />
        ) : (
          <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: '#94a3b8' }}>
            Generating…
          </div>
        )}
        {qrDataUrl && (
          <div style={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: badgeSize, height: badgeSize, borderRadius: badgeSize * 0.28,
            background: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 0 4px white',
          }}>
            <WaIcon size={badgeSize * 0.6} color="white" />
          </div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <WaIcon size={18} />
        <span style={{ fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif', fontWeight: 800, fontSize: 19, color: '#25D366', letterSpacing: '-0.01em' }}>
          WhatsApp
        </span>
      </div>
    </div>
  );
}

export interface RecipientPill {
  label: string;
  bg: string;
  text: string;
}

export interface WhatsAppQrStepProps {
  /** Digits only — the wa.me link is built from this plus `message`. */
  phone: string;
  /**
   * The human-readable text to embed in the link. Pass it (even `''`) to get
   * the editable preview + copy button — the officer-schedule flow always
   * has one. Omit it entirely for a flow with no message (Contacts): the
   * preview UI doesn't render at all rather than showing an empty box.
   */
  message?: string;
  onMessageChange?: (text: string) => void;
  /** Who's being messaged — a contact's name, or the raw number when typed
   * in by hand — shown in the header so it's never ambiguous which QR this is. */
  recipientLabel: string;
  recipientPill?: RecipientPill;
  onBack: () => void;
  onClose: () => void;
}

/**
 * The QR step of "send to WhatsApp" — shared by the officer schedule's picker
 * (which has a pre-filled message) and the standalone Contacts picker (which
 * doesn't), so the two render identically apart from that one difference.
 */
export function WhatsAppQrStep({
  phone, message, onMessageChange, recipientLabel, recipientPill, onBack, onClose,
}: WhatsAppQrStepProps) {
  const [copied, setCopied] = useState(false);
  // The QR carries wa.me (a phone camera opens the app). A click goes straight into WhatsApp Web's chat.
  const link = waMeLink(phone, message);

  const handleCopyMessage = () => {
    if (!message) return;
    navigator.clipboard.writeText(message).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <>
      {/* Header identifies the recipient, not a generic "Send to WhatsApp" —
          otherwise there's nothing on this screen saying who the QR is for. */}
      <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <WaIcon size={20} />
        {recipientPill && (
          <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, flexShrink: 0, whiteSpace: 'nowrap', background: recipientPill.bg, color: recipientPill.text }}>
            {recipientPill.label}
          </span>
        )}
        <span style={{ fontWeight: 700, fontSize: 15, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {recipientLabel}
        </span>
        {/* Bigger hit target than the picker step's close button — this is the
            step most worth being able to dismiss in a hurry with one tap. */}
        <button
          onClick={onClose}
          title="Close"
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#888', padding: 8, margin: -8, lineHeight: 1, flexShrink: 0 }}
        >
          ✕
        </button>
      </div>

      <div style={{ padding: '20px 18px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, overflowY: 'auto', minHeight: 0 }}>
        <button
          onClick={() => window.open(chatLinkForClick(phone, message), '_blank', 'noopener,noreferrer')}
          title="Click to open the chat in WhatsApp"
          style={{ padding: 0, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', lineHeight: 0 }}
        >
          <WhatsAppQrCard link={link} phone={phone} />
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#1e293b', textAlign: 'center' }}>
          <ScanIcon />
          Scan with your phone, or click to open here
        </div>

        {message !== undefined && (
          <div style={{ width: '100%', position: 'relative' }}>
            <textarea
              value={message}
              onChange={(e) => onMessageChange?.(e.target.value)}
              rows={4}
              placeholder="Message…"
              style={{
                width: '100%', boxSizing: 'border-box', padding: '8px 34px 22px 10px', fontSize: 13,
                border: '1px solid #d1d5db', borderRadius: 8, resize: 'vertical',
                fontFamily: 'inherit', color: '#1e293b', outline: 'none',
              }}
            />
            <button
              onClick={handleCopyMessage}
              title="Copy message"
              style={{
                position: 'absolute', bottom: 6, right: 6,
                background: copied ? '#22c55e' : '#f1f5f9', color: copied ? 'white' : '#475569',
                border: 'none', borderRadius: 5, cursor: 'pointer',
                width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <CopyLabelIcon copied={copied} />
            </button>
          </div>
        )}
      </div>

      <div style={{ padding: '10px 18px', borderTop: '1px solid #e5e7eb', flexShrink: 0 }}>
        <button
          onClick={onBack}
          style={{ width: '100%', padding: '9px 0', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
        >
          ← Back
        </button>
      </div>
    </>
  );
}
